import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RT,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ArrowRight } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import { useAuth } from '../contexts/AuthContext';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow, Surcos } from '../components/landing/brand';
import { usePricesSnapshot } from '../hooks/usePricesSnapshot';
import { useMagHistorico } from '../hooks/useMagHistorico';
import { indicesDelMes } from '../helpers/magHistoricoHelper';
import CurrencyToggle, { type Currency } from '../components/public/CurrencyToggle';
import { convertirMonto, formatMonedaSimbolo, type Moneda } from '../libs/moneda';
import { formatNumber } from '../utils/formatNumber';
import { ctaPublico } from '../helpers/ctaPublico';

// /historico-precios — manual de marca v1.0 (estilos en components/landing/brand.css).
// H1 se mantiene: es la página que Google tiene indexada.
//
// Dos fuentes, nunca mezcladas en la misma serie:
// - Mercado de Cañuelas (preseleccionado): informe oficial diario, hacienda de
//   FAENA, agrupado por mes en el build (public/mag-historico.json).
// - Remates de la provincia: promedio mensual de invernada y cría
//   (prices-snapshot.json). Si todavía no hay histórico de Cañuelas, la página
//   arranca acá.

type Rango = 'todo' | '10y' | '5y' | '1y';
type Fuente = 'canuelas' | 'remates';

const RANGOS: { v: Rango; l: string }[] = [
  { v: 'todo', l: 'Todo' },
  { v: '10y', l: '10 años' },
  { v: '5y', l: '5 años' },
  { v: '1y', l: '1 año' },
];

type Cat = { key: string; label: string; color: string; dash?: string };

// Colores del manual; Vacas y Toros van punteadas para distinguirlas de Novillos y Terneras.
// En los remates la clave `novillos` son novillitos de INVERNADA (no novillos de faena).
const CATS_REMATES: Cat[] = [
  { key: 'novillos', label: 'Novillitos (invernada)', color: '#193D2A' },
  { key: 'terneros', label: 'Terneros', color: '#7FB069' },
  { key: 'vaquillonas', label: 'Vaquillonas (invernada)', color: '#D9A441' },
  { key: 'terneras', label: 'Terneras', color: '#6E756B' },
  { key: 'vacas', label: 'Vacas', color: '#2F6B45', dash: '6 4' },
  { key: 'toros', label: 'Toros', color: '#1C1F1A', dash: '2 3' },
];

const CATS_MAG: Cat[] = [
  { key: 'novillos', label: 'Novillos', color: '#193D2A' },
  { key: 'novillitos', label: 'Novillitos', color: '#7FB069' },
  { key: 'vaquillonas', label: 'Vaquillonas', color: '#D9A441' },
  { key: 'vacas', label: 'Vacas', color: '#2F6B45', dash: '6 4' },
  { key: 'toros', label: 'Toros', color: '#1C1F1A', dash: '2 3' },
  { key: 'mej', label: 'MEJ', color: '#6E756B', dash: '2 3' },
];

const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const FUENTES: { v: Fuente; l: string }[] = [
  { v: 'canuelas', l: 'Mercado de Cañuelas' },
  { v: 'remates', l: 'Remates de la provincia (cría e invernada)' },
];

type PuntoSerie = {
  year: number;
  month: number;
  label: string;
  usd: number | null;
  uva: number | null;
  valores: Record<string, number | null>;
};

/** Convierte un mes; sin el dólar o el índice de ESE mes queda en null (hueco) en vez de mostrar pesos. */
function convertirMes(ars: number | null, moneda: Moneda, usd: number | null, uvaMes: number | null, uvaHoy: number | null) {
  if (ars == null) return null;
  if (moneda === 'USD' && !usd) return null;
  if (moneda === 'constante' && !(uvaMes && uvaHoy)) return null;
  return convertirMonto(ars, moneda, usd, uvaHoy, uvaMes);
}

const NIEBLA = '#DDE1D8';

const HistoricoPreciosPage: React.FC = () => {
  const { user } = useAuth();

  useSeoMeta(seoPaginas.historicoPrecios);

  const { data, loading } = usePricesSnapshot();
  const { meses: magMeses, cargando: cargandoMag } = useMagHistorico();
  // Cañuelas preseleccionado cuando hay histórico; si no, los remates.
  const [fuenteElegida, setFuenteElegida] = useState<Fuente | null>(null);
  const fuente: Fuente = fuenteElegida ?? (magMeses.length ? 'canuelas' : 'remates');
  const cats = fuente === 'canuelas' ? CATS_MAG : CATS_REMATES;
  // Arranca en 5 años: con todo el histórico en pesos nominales la inflación aplana la curva.
  const [rango, setRango] = useState<Rango>('5y');
  const [currency, setCurrency] = useState<Currency>('ARS');
  const [activasPorFuente, setActivasPorFuente] = useState<Record<Fuente, Set<string>>>({
    canuelas: new Set(['novillos', 'novillitos', 'vaquillonas']),
    remates: new Set(['terneros', 'novillos', 'vaquillonas']),
  });
  const activas = activasPorFuente[fuente];

  const usdRate = data?.usd.available ? data.usd.rate : null;
  const uvaToday = data?.uva.available ? data.uva.today : null;

  const serie: PuntoSerie[] = useMemo(() => {
    const historia = data?.history ?? [];
    if (fuente === 'remates') {
      return historia.map((h) => ({
        year: h.year,
        month: h.month,
        label: h.label,
        usd: h.usd,
        uva: h.uva,
        valores: Object.fromEntries(CATS_REMATES.map((c) => [c.key, h[c.key as keyof typeof h] as number | null])),
      }));
    }
    return magMeses.map((m) => ({
      year: m.year,
      month: m.month,
      label: m.label,
      ...indicesDelMes(historia, m.year, m.month, usdRate, uvaToday),
      valores: Object.fromEntries(CATS_MAG.map((c) => [c.key, m[c.key as keyof typeof m] as number | null])),
    }));
  }, [data?.history, magMeses, fuente, usdRate, uvaToday]);

  const ventana = useMemo(() => {
    const h = serie;
    if (h.length === 0) return h;
    if (rango === 'todo') return h;
    // Filtrado por fecha (más correcto que cortar N puntos cuando hay meses sin data).
    const yearsBack = rango === '1y' ? 1 : rango === '5y' ? 5 : 10;
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - yearsBack);
    return h.filter((pt) => new Date(pt.year, pt.month - 1, 1) >= cutoff);
  }, [serie, rango]);

  // Cada mes se convierte con la cotización (USD) o el índice (constante) DE ESE MES,
  // igual en el gráfico y en el resumen: así mín/máx y variación tienen sentido histórico.
  const conv = (ars: number | null, h: PuntoSerie) => convertirMes(ars, currency, h.usd, h.uva, uvaToday);

  const chartData = useMemo(
    () =>
      ventana.map((h) => {
        const row: Record<string, number | null | string> = { label: h.label };
        for (const c of cats) row[c.key] = conv(h.valores[c.key], h);
        return row;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ventana, cats, currency, uvaToday]
  );

  const toggleCat = (k: string) =>
    setActivasPorFuente((prev) => {
      const next = new Set(prev[fuente]);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return { ...prev, [fuente]: next };
    });

  // Resumen por categoría activa sobre la ventana seleccionada
  const resumen = useMemo(() => {
    return cats.filter((c) => activas.has(c.key)).map((c) => {
      const vals = ventana.map((h) => conv(h.valores[c.key], h)).filter((v): v is number => v != null);
      const min = vals.length ? Math.min(...vals) : null;
      const max = vals.length ? Math.max(...vals) : null;
      const avg = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      const first = vals.length ? vals[0] : null;
      const last = vals.length ? vals[vals.length - 1] : null;
      const variacion = first && last && first > 0 ? ((last - first) / first) * 100 : null;
      return { ...c, min, max, avg, variacion };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventana, cats, activas, currency, uvaToday]);

  const fmt = (v: number) => formatMonedaSimbolo(v, currency);
  const unidad = currency === 'USD' ? 'US$' : currency === 'constante' ? 'pesos de hoy' : 'ARS';
  // Sin render estático del histórico de Cañuelas, esperar al archivo antes de
  // decidir la fuente (evita mostrar los remates y saltar a Cañuelas).
  const cargandoSerie = loading || (fuenteElegida == null && cargandoMag && magMeses.length === 0);
  const desde = magMeses[0] ? `${MESES_LARGOS[magMeses[0].month - 1]} de ${magMeses[0].year}` : null;

  return (
    <div className="lp">
      <PublicNav />

      <main>
        <section className="lp-ph">
          <div className="lp-wrap">
            <Eyebrow>Promedio mensual por categoría</Eyebrow>
            <h1>Histórico de precios de la hacienda</h1>
            <p className="lp-lead" style={{ maxWidth: 720 }}>
              Precio promedio de cada mes, por categoría y por varios años: la hacienda de faena del Mercado de
              Cañuelas y la de cría e invernada de los remates de la provincia de Buenos Aires. Elegí la fuente, el
              rango y las categorías, y compará en pesos, dólares o pesos de hoy.
            </p>
          </div>
        </section>

        <section className="lp-sec lp-sec--white" style={{ paddingTop: 48 }}>
          <div className="lp-wrap">
            {/* Fuente: nunca se mezclan en la misma serie */}
            <div className="lp-pills" role="group" aria-label="Fuente" style={{ marginBottom: 16 }}>
              <small>Fuente</small>
              {FUENTES.map((f) => (
                <button
                  key={f.v}
                  type="button"
                  className={`lp-pill ${fuente === f.v ? 'on' : ''}`}
                  aria-pressed={fuente === f.v}
                  disabled={f.v === 'canuelas' && magMeses.length === 0}
                  onClick={() => setFuenteElegida(f.v)}
                >
                  {f.l}
                </button>
              ))}
            </div>

            {/* Controles */}
            <div className="lp-sec__h" style={{ alignItems: 'center', marginBottom: 16 }}>
              <div className="lp-pills" role="group" aria-label="Período">
                <small>Período</small>
                {RANGOS.map((r) => (
                  <button
                    key={r.v}
                    type="button"
                    className={`lp-pill ${rango === r.v ? 'on' : ''}`}
                    aria-pressed={rango === r.v}
                    onClick={() => setRango(r.v)}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
              <CurrencyToggle
                value={currency}
                onChange={setCurrency}
                disabled={!usdRate}
                usdRate={usdRate}
                uvaAvailable={!!uvaToday}
              />
            </div>

            <div className="lp-pills" role="group" aria-label="Categorías" style={{ marginBottom: 20 }}>
              <small>Categorías</small>
              {cats.map((c) => {
                const on = activas.has(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    className={`lp-pill ${on ? '' : 'lp-pill--off'}`}
                    aria-pressed={on}
                    onClick={() => toggleCat(c.key)}
                  >
                    <i aria-hidden="true" style={{ background: on ? c.color : NIEBLA }} />
                    {c.label}
                  </button>
                );
              })}
            </div>

            {/* Gráfico */}
            <div className="lp-card lp-chart">
              <p className="lp-note" style={{ marginBottom: 12 }}>
                $/kg promedio mensual · {unidad} ·{' '}
                {fuente === 'canuelas' ? 'Mercado de Cañuelas, hacienda de faena' : 'Remates de la provincia, cría e invernada'}
              </p>
              <div style={{ height: 380 }}>
                {cargandoSerie ? (
                  <div className="lp-skel" style={{ height: '100%' }} />
                ) : chartData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={NIEBLA} />
                      <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: NIEBLA }} tickMargin={8} minTickGap={16} />
                      <YAxis width={70} tickFormatter={(v) => fmt(Number(v))} tickLine={false} axisLine={false} />
                      <RT
                        cursor={{ stroke: NIEBLA, strokeWidth: 1 }}
                        formatter={(v: number | string) => fmt(Number(v))}
                        contentStyle={{ background: '#FFFFFF', border: `1px solid ${NIEBLA}`, borderRadius: 8, fontSize: 13, color: '#1C1F1A' }}
                      />
                      <Legend iconType="plainline" wrapperStyle={{ paddingTop: 12 }} />
                      {cats.filter((c) => activas.has(c.key)).map((c) => (
                        <Line
                          key={c.key}
                          type="monotone"
                          dataKey={c.key}
                          name={c.label}
                          stroke={c.color}
                          strokeWidth={2.5}
                          strokeDasharray={c.dash}
                          dot={false}
                          activeDot={{ r: 4 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="lp-note" style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                    {(fuente === 'canuelas' ? magMeses.length : data?.available)
                      ? 'Sin datos para el rango seleccionado.'
                      : 'Histórico temporalmente no disponible.'}
                  </div>
                )}
              </div>
              <p className="lp-note" style={{ marginTop: 12 }}>
                {fuente === 'canuelas'
                  ? `Fuente: Mercado Agroganadero de Cañuelas, informe oficial diario de precios por categoría. Promedio del mes ponderado por los kilos vendidos${desde ? `, desde ${desde}` : ''}. Es hacienda de faena: para terneros e invernada, elegí los remates de la provincia.`
                  : 'Fuente: promedio mensual de remates de distintas zonas de la provincia de Buenos Aires. Es hacienda de cría e invernada: para novillos y vacas de faena, elegí el Mercado de Cañuelas.'}
              </p>
            </div>

            {/* Resumen */}
            {resumen.length > 0 && chartData.length > 0 && (
              <div className="lp-card" style={{ marginTop: 20 }}>
                <div className="lp-card__h">
                  <h2>Resumen del período</h2>
                  <small className="lp-note">Variación entre el primer y el último mes del rango.</small>
                </div>
                <div className="lp-table-wrap">
                  <table className="lp-table">
                    <thead>
                      <tr>
                        <th scope="col">Categoría</th>
                        <th scope="col" className="num">Promedio</th>
                        <th scope="col" className="num">Mínimo</th>
                        <th scope="col" className="num">Máximo</th>
                        <th scope="col" className="num">Variación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.map((r) => (
                        <tr key={r.key}>
                          <td>{r.label}</td>
                          <td className="num">{r.avg != null ? fmt(r.avg) : '—'}</td>
                          <td className="num">{r.min != null ? fmt(r.min) : '—'}</td>
                          <td className="num">{r.max != null ? fmt(r.max) : '—'}</td>
                          <td className={`num ${r.variacion == null ? '' : r.variacion >= 0 ? 'lp-pos' : 'lp-neg'}`}>
                            {r.variacion == null ? '—' : `${r.variacion >= 0 ? '+' : ''}${formatNumber(r.variacion, { decimals: 1 })}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="lp-sec lp-dark">
          <div className="lp-wrap">
            <div className="lp-sec-h" style={{ maxWidth: 720 }}>
              <Eyebrow>Con tu cuenta gratis</Eyebrow>
              <h2 className="lp-h2">Usá estos precios con tus animales reales</h2>
              <p className="lp-lead">
                En MiCampoDigital, el valor de tu inventario sale de tus animales, con los pesos de tu balanza y los
                precios de referencia de cada mes.
              </p>
            </div>
            <div className="lp-ctas">
              <Link className="lp-btn lp-btn--primary" to={ctaPublico(user).to}>
                {ctaPublico(user).label} <ArrowRight aria-hidden="true" />
              </Link>
              <Link className="lp-btn lp-btn--outline" to="/calculadora-inventario">
                Probar la calculadora de inventario
              </Link>
            </div>
          </div>
          <Surcos color="blanco" style={{ right: -120, bottom: -260, width: 860 }} />
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default HistoricoPreciosPage;
